import { useState, useCallback } from 'react';
import type { MouseEvent } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';

import Checkbox from 'views/ui/form_elements/checkbox';
import { BaseModal } from '@anyroad/modal';

import {
  WaiverContainer,
  WaiverCheckbox,
  CheckboxWrapper,
  WaiverContent,
  WaiverText,
  WaiverLink,
  WaiverSubtext,
  ErrorMessage,
} from './styles';

const SquareCheckboxWrapper = styled(CheckboxWrapper)`
  .Checkbox-indicator {
    border-radius: 0;
  }

  .Checkbox-indicator-fill {
    border-radius: 0;
  }
`;

interface WaiverProps {
  question: any;
  value: any;
  onChange: (value: any) => void;
  errors: any;
  dataTestId?: string;
}

export const Waiver = ({
  question,
  value,
  onChange,
  errors,
  dataTestId,
}: WaiverProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const hasError = useCallback(() => {
    return errors?.length >= 1;
  }, [errors]);

  const renderErrors = useCallback(() => {
    if (hasError()) {
      let error = errors[0];
      if (
        question.required &&
        question.input_type === 'waiver' &&
        error.includes('Please select an option')
      ) {
        error = `${formatMessage({ defaultMessage: 'Please select', description: 'Error message when no option is selected' })}.`;
      }
      return error;
    }
    return null;
  }, [hasError, errors, question]);

  const handleLinkClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const WaiverModalContent = ({ onClose }: { onClose: () => void }) => {
    const { popup_link_name: popupLinkName, popup_link_text: popupLinkText } =
      question;
    return (
      <div className="Waiver-Modal-content">
        <h2>{popupLinkName}</h2>
        {popupLinkText && (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(popupLinkText) }}
          />
        )}
        <button type="button" onClick={onClose}>
          {formatMessage({
            defaultMessage: 'Close',
            description: 'Button text to close the modal dialog',
          })}
        </button>
      </div>
    );
  };

  const WaiverModal = BaseModal(WaiverModalContent, {
    className: 'small Waiver-Modal',
    padding: '32px',
    width: '700px',
  });

  if (!question) {
    return null;
  }

  const val = value === 'yes';

  let popupLink = null;
  if (question.popup_link_name) {
    popupLink = (
      <WaiverLink onClick={handleLinkClick}>
        {question.popup_link_name}
      </WaiverLink>
    );
  }

  return (
    <WaiverContainer
      className="Waiver-Container"
      hasError={hasError()}
      data-testid={dataTestId}
    >
      <WaiverCheckbox
        className="Waiver-Checkbox"
        data-testid="waiver-policy-checkbox"
        hasError={hasError()}
      >
        <SquareCheckboxWrapper>
          <Checkbox
            value={val}
            onChange={(_name: string, newValue: boolean) =>
              onChange(newValue ? 'yes' : 'no')
            }
            linkClick={handleLinkClick}
            popup_link_name={question.popup_link_name}
            popup_link_text={question.popup_link_text}
            label=""
          />
        </SquareCheckboxWrapper>
        <WaiverContent className="Waiver-Content" data-testid="waiver-content">
          <WaiverText data-testid="waiver-text">
            {question.text} {popupLink}
          </WaiverText>
          {question.subtext && (
            <WaiverSubtext
              data-testid="waiver-subtext"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(question.subtext),
              }}
            />
          )}
        </WaiverContent>
      </WaiverCheckbox>
      {renderErrors() && (
        <ErrorMessage data-testid="waiver-error">{renderErrors()}</ErrorMessage>
      )}
      <WaiverModal isOpen={isModalOpen} onClose={closeModal} />
    </WaiverContainer>
  );
};
